import React, { useEffect, useState } from "react";
import axios from "axios";

export default function EditDashboard({ onBackToDashboard }) {
  const [config, setConfig] = useState(null);
  const [zoneData, setZoneData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');

  // Fetch config from backend on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = {
          Authorization: `Bearer ${token}`,
        };

        // Fetch basic config
        const configResponse = await axios.get(
          process.env.REACT_APP_API_URL + "/api/config",
          { headers }
        );
        setConfig(configResponse.data);

        // Fetch zone data
        const zoneResponse = await axios.get(
          process.env.REACT_APP_API_URL + "/api/config/zones",
          { headers }
        );
        setZoneData(zoneResponse.data);
      } catch (err) {
        setError(err.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Handle basic config changes
  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setConfig((prev) => ({ ...prev, [name]: value }));
  };

  // Handle zone data changes
  const handleZoneChange = (index, field, value) => {
    const updatedZones = [...zoneData];
    if (field === 'construction') {
      updatedZones[index][field] = Number(value);
    } else if (field === 'marketingMultiplier') {
      updatedZones[index][field] = Number(value);
      // Update marketing percentage display
      updatedZones[index].marketing = `${(Number(value) * 100).toFixed(1)}%`;
    } else {
      updatedZones[index][field] = value;
    }
    setZoneData(updatedZones);
  };

  // Handle save basic config
  const handleSaveConfig = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        process.env.REACT_APP_API_URL + "/api/config",
        config,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      alert("Configuration updated successfully!");
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle save zone data
  const handleSaveZones = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        process.env.REACT_APP_API_URL + "/api/config/zones",
        zoneData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      alert("Zone data updated successfully!");
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="dashboard-container">
      <div className="calculation-card">
        <div className="card-header">
          <h2>Loading configuration...</h2>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className="dashboard-container">
      <div className="calculation-card">
        <div className="card-header">
          <h2>Error: {error}</h2>
        </div>
        <div className="padding-20">
          <button onClick={onBackToDashboard} className="btn btn-secondary">
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="dashboard-container">
      <div className="calculation-card">
        <div className="card-header">
          <h2>⚙️ Edit Configuration</h2>
        </div>

        <div className="padding-20">
          {/* Tab Navigation */}
          <div className="tab-navigation" style={{ marginBottom: '2rem' }}>
            <button
              className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
              onClick={() => setActiveTab('basic')}
            >
              Basic Settings
            </button>
            <button
              className={`tab-button ${activeTab === 'zones' ? 'active' : ''}`}
              onClick={() => setActiveTab('zones')}
            >
              Zone Settings
            </button>
          </div>

          {/* Basic Configuration Tab */}
          {activeTab === 'basic' && config && (
            <div className="config-section">
              <h3>Basic Configuration Values</h3>
              <div className="input-grid">
                <div className="input-group">
                  <label className="input-label">Approval Value</label>
                  <input
                    type="number"
                    name="allrovalVal"
                    value={config.allrovalVal}
                    onChange={handleConfigChange}
                    className="calc-input"
                  />
                </div>
                <div className="input-group">
                  <label className="input-label">Cess Value</label>
                  <input
                    type="number"
                    name="cessValue"
                    value={config.cessValue}
                    onChange={handleConfigChange}
                    className="calc-input"
                  />
                </div>
                <div className="input-group">
                  <label className="input-label">Maintenance Value</label>
                  <input
                    type="number"
                    name="maintananceValue"
                    value={config.maintananceValue}
                    onChange={handleConfigChange}
                    className="calc-input"
                  />
                </div>
                <div className="input-group">
                  <label className="input-label">Interest Rate</label>
                  <input
                    type="number"
                    step="0.001"
                    name="interest"
                    value={config.interest}
                    onChange={handleConfigChange}
                    className="calc-input"
                  />
                </div>
                <div className="input-group">
                  <label className="input-label">HR & Admin Rate</label>
                  <input
                    type="number"
                    step="0.001"
                    name="hrAndAdmin"
                    value={config.hrAndAdmin}
                    onChange={handleConfigChange}
                    className="calc-input"
                  />
                </div>
                <div className="input-group">
                  <label className="input-label">LO Share Interest</label>
                  <input
                    type="number"
                    step="0.001"
                    name="LoshareInterest"
                    value={config.LoshareInterest}
                    onChange={handleConfigChange}
                    className="calc-input"
                  />
                </div>
                <div className="input-group">
                  <label className="input-label">LO Share HR & Admin</label>
                  <input
                    type="number"
                    step="0.001"
                    name="loShareHrAndAdmin"
                    value={config.loShareHrAndAdmin}
                    onChange={handleConfigChange}
                    className="calc-input"
                  />
                </div>
              </div>
              <button
                onClick={handleSaveConfig}
                disabled={saving}
                className="btn btn-primary"
                style={{ marginTop: '1rem' }}
              >
                {saving ? "Saving..." : "💾 Save Basic Config"}
              </button>
            </div>
          )}

          {/* Zone Configuration Tab */}
          {activeTab === 'zones' && (
            <div className="config-section">
              <h3>Zone Configuration</h3>
              <div className="zone-grid">
                {zoneData.map((zone, index) => (
                  <div key={zone.zone} className="zone-card">
                    <h4>{zone.zone}</h4>
                    <div className="input-group">
                      <label className="input-label">Construction Cost</label>
                      <input
                        type="number"
                        value={zone.construction}
                        onChange={(e) => handleZoneChange(index, 'construction', e.target.value)}
                        className="calc-input"
                      />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Marketing Multiplier</label>
                      <input
                        type="number"
                        step="0.001"
                        value={zone.marketingMultiplier}
                        onChange={(e) => handleZoneChange(index, 'marketingMultiplier', e.target.value)}
                        className="calc-input"
                      />
                      <small style={{ color: '#666', fontSize: '0.8rem' }}>
                        Current: {zone.marketing}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={handleSaveZones}
                disabled={saving}
                className="btn btn-primary"
                style={{ marginTop: '1rem' }}
              >
                {saving ? "Saving..." : "💾 Save Zone Config"}
              </button>
            </div>
          )}

          <div style={{ marginTop: "2rem", textAlign: 'center' }}>
            <button
              onClick={onBackToDashboard}
              className="btn btn-secondary"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}